import { type CSSProperties, memo } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { SelectChipRemoveButton } from '~/components/Select/components/SelectChipRemoveButton';
import { SELECT_CHIP } from '~/components/Select/shared/testIDs';
import { type DefaultChipType } from '~/components/Select/shared/types';

export const selectChip = tailwindVariants({
  base: 'my-[3px] mr-1.5 flex w-auto items-center justify-start rounded-full ads:bg-ads-gray-4 bg-gray-4 transition-colors',
  variants: {
    size: {
      medium: 'px-1 py-0 text-12',
      large: 'px-2 py-0.5 text-12',
      'extra-large': 'px-3 py-1.5 text-14',
    },
  },
  defaultVariants: {
    size: 'large',
  },
});

type SelectChipVariants = VariantProps<typeof selectChip>;

type MultiSelectChipProps<T extends DefaultChipType> = {
  chip: T;
  onDeselect?: (deselected: T) => void;
  'data-testid'?: string;
} & SelectChipVariants;

const makeCustomStyles = (chip: DefaultChipType): CSSProperties => ({
  ...(chip.backgroundHexColor && { backgroundColor: chip.backgroundHexColor }),
  ...(chip.borderRadius && { borderRadius: chip.borderRadius }),
});

const _SelectChip = <T extends DefaultChipType>({
  chip,
  onDeselect,
  'data-testid': testId = SELECT_CHIP,
  size = 'large',
}: MultiSelectChipProps<T>) => {
  return (
    <div className={selectChip({ size })} style={makeCustomStyles(chip)}>
      <div
        className="truncate font-medium ads:text-secondary text-[length:inherit] text-gray-11"
        // biome-ignore lint/plugin: Cypress selects this chip by the option label baked into the test id
        data-testid={`${testId}-${chip.label}`}
        style={{
          color: chip.fontHexColor,
        }}
      >
        {chip.label}
      </div>
      {!!onDeselect && <SelectChipRemoveButton chip={chip} onClick={onDeselect} />}
    </div>
  );
};

// biome-ignore lint/plugin: memo erases the generic chip props, so the assertion restores SelectChip's public type
export const SelectChip = memo(_SelectChip) as typeof _SelectChip;
